import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { SSOConfig, SSOConfigUpdate } from '@/types';

interface CustomerDetails {
  id: string;
  name: string;
  subscription_plan?: string;
  sso_config?: Partial<SSOConfig>;
}

export function CustomerSSOPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [ssoConfig, setSsoConfig] = useState<SSOConfigUpdate>({
    provider: 'google',
    status: 'inactive',
    config: {
      client_id: '',
      client_secret: '',
      domain: '',
      metadata_url: '',
    },
  });

  useEffect(() => {
    checkAdminAccess();
    fetchCustomerDetails();
  }, [customerId]);

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('role, company_id')
      .eq('id', user.id)
      .single();

    // Check if the user is an admin or the owner of the company
    const isAdmin = profile?.role === 'admin';
    const isCompanyOwner = profile?.company_id === customerId;

    if (!isAdmin && !isCompanyOwner) {
      navigate('/dashboard');
      toast.error('Unauthorized access');
      return;
    }

    // If not an admin, ensure the user is accessing their own company
    if (!isAdmin && !isCompanyOwner) {
      navigate('/dashboard');
      toast.error('You can only configure SSO for your own company');
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          subscriptions (plan),
          sso_configs (
            id,
            provider,
            status,
            config
          )
        `)
        .eq('id', customerId)
        .single();

      if (companyError) throw companyError;

      setCustomer({
        id: company.id,
        name: company.name,
        subscription_plan: company.subscriptions?.[0]?.plan,
        sso_config: company.sso_configs?.[0],
      });

      if (company.sso_configs?.[0]) {
        setSsoConfig(company.sso_configs[0]);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSSO = async () => {
    try {
      setSaving(true);

      // Validate subscription plan
      const allowedPlans = ['pro', 'enterprise'];
      if (!customer?.subscription_plan || !allowedPlans.includes(customer.subscription_plan)) {
        toast.error('SSO is only available for Pro and Enterprise plans');
        return;
      }

      // Validate required configuration fields
      const requiredFields = ['client_id', 'client_secret', 'domain'];
      const missingFields = requiredFields.filter(field =>
        !ssoConfig.config?.[field as keyof typeof ssoConfig.config]
      );

      if (missingFields.length > 0) {
        toast.error(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Determine SSO status based on plan
      const finalStatus = customer.subscription_plan === 'enterprise'
        ? ssoConfig.status
        : (ssoConfig.status === 'active' ? 'active' : 'inactive');

      // Prepare SSO configuration data
      const ssoConfigData = {
        company_id: customer.id,
        provider: ssoConfig.provider,
        status: finalStatus,
        config: {
          ...ssoConfig.config,
          // Ensure only allowed providers for non-enterprise plans
          provider: customer.subscription_plan === 'enterprise'
            ? ssoConfig.provider
            : (ssoConfig.provider === 'google' || ssoConfig.provider === 'github'
              ? ssoConfig.provider
              : 'google')
        }
      };

      if (customer.sso_config?.id) {
        // Update existing SSO config
        const { error } = await supabase
          .from('sso_configs')
          .update(ssoConfigData)
          .eq('id', customer.sso_config.id);

        if (error) throw error;
      } else {
        // Create new SSO config
        const { error } = await supabase
          .from('sso_configs')
          .insert([ssoConfigData]);

        if (error) throw error;
      }

      toast.success('SSO configuration updated successfully');
      fetchCustomerDetails();
    } catch (error) {
      console.error('Error updating SSO config:', error);
      toast.error('Failed to update SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (key: string, value: string) => {
    setSsoConfig((prev: SSOConfigUpdate) => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value,
      },
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  const isEnterprise = customer?.subscription_plan === 'enterprise';
  const isPro = customer?.subscription_plan === 'pro';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{customer?.name}</h1>
          <p className="text-muted-foreground">Configure SSO integration</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/customers')}>
          Back to Customers
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>SSO Configuration</CardTitle>
          <Badge variant={ssoConfig.status === 'active' ? 'default' : 'secondary'}>
            {ssoConfig.status === 'active' ? 'ENABLED' : 'DISABLED'}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Provider</label>
            <Select
              value={ssoConfig.provider}
              onValueChange={(value: SSOConfig['provider']) => setSsoConfig({ ...ssoConfig, provider: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select SSO provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="github">GitHub</SelectItem>
                <SelectItem value="google">Google Workspace</SelectItem>
                <SelectItem value="okta">Okta</SelectItem>
                <SelectItem value="azure">Azure AD</SelectItem>
                {isEnterprise && <SelectItem value="custom">Custom SAML</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <Select
              value={ssoConfig.status}
              onValueChange={(value: 'active' | 'inactive') => setSsoConfig({ ...ssoConfig, status: value })}
              disabled={!isPro && !isEnterprise}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Enabled</SelectItem>
                <SelectItem value="inactive">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 pt-4">
            <h3 className="text-lg font-medium">Provider Configuration</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Client ID</label>
                <Input
                  value={ssoConfig.config?.client_id || ''}
                  onChange={(e) => handleConfigChange('client_id', e.target.value)}
                  disabled={!isPro && !isEnterprise}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Client Secret</label>
                <Input
                  type="password"
                  value={ssoConfig.config?.client_secret || ''}
                  onChange={(e) => handleConfigChange('client_secret', e.target.value)}
                  disabled={!isPro && !isEnterprise}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Domain</label>
                <Input
                  value={ssoConfig.config?.domain || ''}
                  onChange={(e) => handleConfigChange('domain', e.target.value)}
                  placeholder="company.com"
                  disabled={!isPro && !isEnterprise}
                />
              </div>

              {ssoConfig.provider === 'custom' && (
                <div>
                  <label className="text-sm font-medium">Metadata URL</label>
                  <Input
                    value={ssoConfig.config?.metadata_url || ''}
                    onChange={(e) => handleConfigChange('metadata_url', e.target.value)}
                    placeholder="https://example.com/saml/metadata"
                    disabled={!isEnterprise}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleSaveSSO}
              disabled={saving || (!isPro && !isEnterprise)}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}